<script>
import { mapGetters } from 'vuex';

import SearchHeader from './SearchHeader.vue';
import ChunkCard from '../components/ChunkCard.vue';
import SearchResultSection from './SearchResultSection.vue';
import ButtonNext from 'next/button/Button.vue';

export default {
  components: {
    SearchHeader,
    ChunkCard,
    SearchResultSection,
    ButtonNext,
  },
  props: {
    bot: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['back', 'deleteChunk', 'toggleChunkStatus', 'editChunk'],
  data() {
    return {
      loading: {},
      query: '',
      selectedChunk: {},
      showEditChunkPopup: false,
      showDeleteChunkConfirmationPopup: false,
    };
  },
  computed: {
    ...mapGetters({
      searchResults: 'wgptBots/getSearchResults',
      searchMeta: 'wgptBots/getSearchMeta',
      uiFlags: 'wgptBots/getUIFlags',
      botChunkCardsUiFlags: 'wgptBotSourceChunks/getUIFlags',
    }),
    totalSearchResultsCount() {
      return this.searchMeta.total ?? 0;
    },
    currentSearchResultsPage() {
      return this.searchMeta.page ?? 1;
    },
    totalSearchResultsPages() {
      return this.searchMeta.limit
        ? Math.ceil(this.searchMeta.total / this.searchMeta.limit)
        : 1;
    },
    showEmptySearchResults() {
      return (
        this.totalSearchResultsCount === 0 &&
        this.uiFlags.isSearchCompleted &&
        !this.uiFlags.isFetching &&
        this.query
      );
    },
    showResultsSection() {
      return (
        (this.uiFlags.isSearchCompleted &&
          this.totalSearchResultsCount !== 0) ||
        this.uiFlags.isFetching
      );
    },
  },
  watch: {
    bot() {
      this.setDefaults();
    },
  },
  mounted() {
    this.$store.dispatch('wgptBots/clearSearch');
  },
  beforeUnmount() {
    this.query = '';
  },
  methods: {
    onSearch(q, page = 1, limit = 5) {
      this.query = q;
      if (!q) return;
      const id = this.bot.id;
      this.$store.dispatch('wgptBots/search', { id, q, page, limit });
    },
    nextPage() {
      this.onSearch(this.query, this.searchMeta.page + 1);
    },
    previousPage() {
      this.onSearch(this.query, this.searchMeta.page - 1);
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto h-full">
    <div class="h-full overflow-hidden">
      <section class="search-root">
        <header class="bg-white dark:bg-transparent flex flex-col gap-4">
          <div>
            <ButtonNext
              label="Back"
              icon="i-lucide-chevron-left"
              variant="faded"
              size="sm"
              @click="$emit('back')"
            />
          </div>
          <SearchHeader @search="onSearch" />
        </header>
        <div class="search-results">
          <SearchResultSection
            v-if="query"
            :title="
              $t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.SEARCH.HEADER.TITLE')
            "
            :empty="searchMeta.total === 0"
            :query="query"
            show-title
            :is-fetching="
              uiFlags.isSearching ||
              botChunkCardsUiFlags.isUpdating ||
              botChunkCardsUiFlags.isDeleting
            "
            :page="currentSearchResultsPage"
            :total-pages="totalSearchResultsPages"
            @next-page="nextPage"
            @previous-page="previousPage"
          >
            <ChunkCard
              v-for="chunkRecord of searchResults"
              :key="chunkRecord.id"
              :chunk="chunkRecord"
              @delete="$emit('deleteChunk', chunkRecord)"
              @toggle-status="$emit('toggleChunkStatus', chunkRecord)"
              @edit="$emit('editChunk', chunkRecord)"
            />
          </SearchResultSection>

          <div v-else class="empty text-center">
            <fluent-icon icon="info" size="16px" class="icon inline" />
            <p class="empty-state__text">
              {{
                $t(
                  'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.SEARCH.EMPTY_STATE_DEFAULT'
                )
              }}
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  ::v-deep {
    input {
      @apply lowercase;
    }
  }
}
.search-root {
  @apply flex my-4 p-4 mt-6 relative mx-auto max-w-[45rem] min-h-[20rem] flex-col w-full h-full;

  .search-results {
    @apply flex-grow h-full overflow-y-auto py-0;

    .search-results {
      @apply flex-grow h-full overflow-y-auto py-0 px-2;
    }
  }
}

.empty {
  @apply flex items-center justify-center py-6 px-4 my-8 rounded-md;
  .icon {
    @apply text-slate-500 dark:text-slate-300;
  }
  .empty-state__text {
    @apply text-slate-500 dark:text-slate-300 text-center my-0 mx-2;
  }
}
</style>
